import { API } from '../../api';
import {
  Alert,
  Button,
  Cascader,
  Col,
  Divider,
  Dropdown,
  Input,
  Menu,
  Modal,
  Row,
  Switch,
  notification,
} from 'antd';
import {
  CopyOutlined,
  ClearOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  ExportOutlined,
  FileAddOutlined,
  PlusOutlined,
  RedoOutlined,
  SaveOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { EXTENSION_CODEABLE_CONCEPT } from '../../consts';
import { copyToClipboard, exportAllMappings, getImplementationGuide, getStructureDefProfiles, getUniquePathName, handleError, logAction, logGAEvent } from '../../utils/util';
import { v4 as uuidv4 } from 'uuid';
import { withRouter } from 'react-router-dom';
import AddMappingModal from './addMappingModal';
import CsvDownload from 'react-json-to-csv';
import DataSourceCreate from '../dataSources/dataSourceCreate';
import Hotkey from '../hotkeys';
import React from 'react';
import ReviewDrawer from '../mappingReview/reviewDrawer';
import DiffModal from '../diffViewer/diffModal';

const { confirm } = Modal;

class TableHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showSaveModal: false,
      duplicateNamespaceName: null,
      saveTemplateNamespaceName: props.state.namespace,
      newFileValue: null,
      loading: false,
      showModal: false,
      showRequesterChecklist: false,
      showDiffViewer: false,
      profile_url: null,
      profiles: null,
    };
  }


  showDeleteConfirm = () => {
    const { version, resourcetype, namespace, file, dataSource, versionOptions } = this.props.state;
    logGAEvent('Delete Mappings Bulk');
    const content = (
      <>
        This will delete all mappings matching criteria below ({dataSource.length}): <br />
        <br />
        <b>Version:</b> {version}
        <br />
        <b>ResourceType:</b> {resourcetype}
        <br />
        <b>Namespace:</b> {namespace}
        <br />
        <b>File:</b> {file}
      </>
    );

    const successMsg = `Deleted ${this.props.state.dataSource.length} mappings.`;

    confirm({
      title: 'Are you sure?',
      icon: <WarningOutlined />,
      content,
      okText: 'Delete Mappings',
      okType: 'danger',
      okButtonProps: { 'data-cy': 'delete-mappings-bulk-confirm-btn' },
      cancelText: 'Cancel',
      onOk: async () => {
        const ids = this.props.state.dataSource.map(mapping => mapping.id);

        try {
          await API.post('/mapping?delete', { ids });
          notification.success({ message: successMsg });
          // after deleting, reset version and namespace so table is not blank on reload
          if (versionOptions.includes('latest')) {
            this.props.setParentState({ version: 'latest', namespace: 'default' }, () =>
              this.props.getData(),
            );
          } else {
            this.props.getData();
          }
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  showDuplicateConfirm = () => {
    const { version, resourcetype, namespace, file, dataSource, client } = this.props.state;
    const content = (
      <>
        This will duplicate all mappings matching criteria below: ({dataSource.length}): <br />
        <br />
        <b>Version:</b> {version}
        <br />
        <b>ResourceType:</b> {resourcetype}
        <br />
        <b>Namespace:</b> {namespace}
        <br />
        <b>File:</b> {file}
        <br />
        <br />
        Please enter a unique namespace value:
        <Input
          data-cy="duplicate-modal-namespace-input"
          placeholder="Namespace"
          onChange={e => this.setState({ duplicateNamespaceName: e.target.value })}
        />
        <small>
          The combination of namespace and file must be unique
          <br />
          <br />
        </small>
        Please enter a file value:
        <Input
          data-cy="duplicate-modal-file-input"
          placeholder="File"
          onChange={e => this.setState({ newFileValue: e.target.value })}
        />
      </>
    );

    const successMsg = `Duplicated ${this.props.state.dataSource.length} mappings.`;

    confirm({
      title: 'Are you sure?',
      icon: <CopyOutlined />,
      content,
      okText: 'Duplicate Mappings',
      okType: 'primary',
      cancelText: 'Cancel',
      okButtonProps: { 'data-cy': 'duplicate-mappings-btn' },
      onOk: () => {
        const dupeVersion = (process.env.REACT_APP_FEATURE_MAP_STAGING && version === 'latest') ? 'draft' : version;
        const duplicate = {
          client_id: client.id,
          mappings: dataSource,
          version: dupeVersion,
          resourcetype,
          namespace,
          file,
        };
        duplicate.mappings.forEach(row => {
          row.namespace = this.state.duplicateNamespaceName;
          row.version = dupeVersion;

          if (this.state.newFileValue) {
            row.file = this.state.newFileValue;
          }
        });

        return API.post('/mapping', duplicate)
          .then(() => {
            notification.success({ message: successMsg });

            this.props.setParentState(
              {
                namespace: this.state.duplicateNamespaceName,
                file: this.state.newFileValue,
                version: dupeVersion,
              },
              () => this.props.getData(),
            );
          })
          .catch(err => handleError(err));
      },
    });
  };

  showSaveTemplateConfirm = () => {
    const { version, resourcetype, namespace, file, dataSource } = this.props.state;

    if (dataSource.length === 0) {
      return notification.error({
        description:
          'No mappings found. Please select a version, resourceType, namespace, and file ' +
          'with existing mappings.',
      });
    }

    this.setState({ saveTemplateNamespaceName: namespace });

    const content = (
      <>
        This will create a template from the {dataSource.length} mappings matching criteria below:
        <br />
        <br />
        <b>Version:</b> {version}
        <br />
        <b>ResourceType:</b> {resourcetype}
        <br />
        <b>Namespace:</b> {namespace}
        <br />
        <b>File:</b> {file}
        <br />
        <br />
        Namespace for template:
        <Input
          data-cy="save-template-modal-namespace-input"
          defaultValue={namespace}
          onChange={e => this.setState({ saveTemplateNamespaceName: e.target.value })}
        />
      </>
    );

    confirm({
      title: 'Are you sure?',
      icon: <CopyOutlined />,
      content,
      okText: 'Save As Template',
      okType: 'primary',
      cancelText: 'Cancel',
      okButtonProps: { 'data-cy': 'save-template-confirm-btn' },
      onOk: () => {
        const templateData = {
          mappings: dataSource,
          create_template: true,
        };

        if (this.state.saveTemplateNamespaceName !== namespace) {
          templateData.mappings.forEach(mapping => {
            mapping.namespace = this.state.saveTemplateNamespaceName;
          });
        }

        return API.post('/mapping', templateData)
          .then(() => {
            notification.success({
              message: 'Success',
              description: 'Created new template.',
            });
          })
          .catch(err => handleError(err));
      },
    });
  };

  showMergeVersionConfirm = () => {
    const { version, resourcetype, namespace, file, client } = this.props.state;

    const content = (
      <>
        <Alert message="This action will impact NiFi data flows." />
        <br />
        The mappings matching criteria below will be set as the 'latest' version. The current 'latest' version will be archived.
        <br />
        <br />
        <b>Version:</b> {version}
        <br />
        <b>ResourceType:</b> {resourcetype}
        <br />
        <b>Namespace:</b> {namespace}
        <br />
        <b>File:</b> {file}
        <br />
      </>
    );

    confirm({
      title: 'Are you sure?',
      icon: <CopyOutlined />,
      content,
      okText: 'Merge to Latest',
      okType: 'primary',
      cancelText: 'Cancel',
      okButtonProps: { 'data-cy': 'merge-mappings-confirm-btn' },
      onOk: () => {
        const postBody = {
          client_id: client.id,
          version,
          resourcetype,
          namespace,
          file,
        };

        return API.post('/mapping/merge', postBody)
          .then(() => {
            notification.success({
              message: 'Success',
              description: `Set version '${version} to 'latest'`,
            });
            this.props.setParentState({ version: 'latest' });
          })
          .catch(err => handleError(err));
      },
    });
  };

  addStructureDefElements = record => {
    let newElements;

    if (record.paths != null && record.paths.length > 0) {
      newElements = record.paths.map(path => {
        const uniquePath = getUniquePathName(path.path, this.props.state.dataSource);
        let value = '';

        if (path.allowed_values !== null) {
          if (path.allowed_values.length === 1) {
            value = path.allowed_values[0];
          }
        }

        return {
          path: uniquePath,
          id: uuidv4(),
          client_id: this.props.state.client.id,
          value,
          resourcetype: this.props.state.resourcetype,
          namespace: this.props.state.namespace,
          file: this.props.state.file,
          driving: null,
          version: this.props.state.version,
          editable: true,
        };
      });
    } else {
      newElements = {
        path: record.root_element,
        id: uuidv4(),
        client_id: this.props.state.client.id,
        value: '',
        resourcetype: this.props.state.resourcetype,
        namespace: this.props.state.namespace,
        file: this.props.state.file,
        driving: null,
        version: this.props.state.version,
        editable: true,
      };
    }

    this.handleAddMultipleRows(newElements);
    this.setState({ addMappingModal: false });
  };

  insertMultipleRows = async type => {
    let isError = false;
    const newMappings = type.map(path => {
      const uniquePath = getUniquePathName(path, this.props.state.dataSource);
      if (uniquePath == null) isError = true;

      return {
        id: uuidv4(),
        client_id: this.props.state.client.id,
        path: uniquePath,
        value: '',
        resourcetype: this.props.state.resourcetype,
        namespace: this.props.state.namespace,
        file: this.props.state.file,
        driving: null,
        version: this.props.state.version,
        editable: true,
      };
    });
    if (isError) return;
    this.handleAddMultipleRows(newMappings);
  };

  clearMappingCache = () => {
    this.props.setParentState({ loading: true });

    API.put('/mapping/cache', { client_id: this.props.state.client.id })
      .then(res => {
        notification.success({
          description: `Cleared the JsonToFHIR Lambda FHIRMapping cache!`,
        });
      })
      .catch(err => handleError(err))
      .finally(() => this.props.setParentState({ loading: false }));
    };

  handleAddMultipleRows = newMappings => {
    const { dataSource } = this.props.state;
    this.props.setParentState({ loading: true });
    API.post('/mapping', { mappings: newMappings })
      .then(res => {
        notification.success({
          description: `Added ${newMappings.length} mappings`,
        });
        this.props.setParentState({
          loading: false,
          dataSource: [...newMappings, ...dataSource],
        });
      })
      .catch(err => handleError(err))
      .finally(() => this.props.setParentState({ loading: false }));
  };

  /**
   * Because there is a uniqueness constraint on file, path, namespace, version, client_id and resourcetype,
   * need to autogenerate a new row with some random values.
   * @returns {Promise<void>}
   */
  handleAdd = async newData => {
    const { dataSource } = this.props.state;

    this.props.setParentState(
      {
        dataSource: [newData, ...dataSource],
      },
      () => {
        API.post(`/mapping/${newData.id}`, newData)
          .then(res => {
            notification.success({
              description: `Added Row (${res.status})`,
              duration: 2,
            });
          })
          .catch(err => handleError(err));
      },
    );
  };

  dropdownRender = menus => {
    return (
      <div>
        {menus}
        <Divider style={{ margin: 0 }} />
        <div style={{ padding: 8 }}>
          Select mapping by {this.props.state.byResourcetype ? 'resourcetype' : 'file'}
        </div>
      </div>
    );
  };

  /**
   * Logs when user downloads current mappings
   */
  handleDownloadCurrentMappings = async () => {
    const {
      file,
      namespace,
      resourcetype,
      version,
      client: { id },
    } = this.props.state;
    await logAction({
      client_id: id,
      action: 'Export Current Mappings',
      version,
      resourcetype,
      namespace,
      file,
    }).catch(err => handleError(err));
  };

  setParentState = (obj, callback) => this.setState(obj, callback);

  addProfileToMapping = (profile) => {
    const metaRow = {
      business_term: null,
      driving: null,
      editable: true,
      must_support: null,
      notes: null,
      id: uuidv4(),
      number: null,
      slice: null,
      type: null,
      file: this.props.state.file ? this.props.state.file : '',
      path: 'meta.profile[0]',
      value: `"${profile}"`,
      client_id: this.props.state.client.id,
      version: this.props.state.version,
      resourcetype: this.props.state.resourcetype,
      namespace: this.props.state.namespace,
    };
    this.handleAdd(metaRow).then(res => {
      this.props.getData();
    })

  };

  closeModalNew = () => this.setState({ showModal: false });

  addDataSource = newDataSource => {
    if (newDataSource.resourcetype && newDataSource.namespace) {
      this.props.setParentState(
        {
          resourcetype: newDataSource.resourcetype,
          namespace: newDataSource.namespace,
          file: newDataSource.name,
        },
        () => this.props.getData(),
      );
    } else {
      this.props.setParentState(
        {
          file: newDataSource.name,
        },
        () => this.props.getData(),
      );
    }
  };

  handleReviewBtnClick = async () => {
    const {
      fileId: file_id,
      version,
      client: { id },
      mappingReview,
      resourcetype,
      namespace,
    } = this.props.state;

    if (!mappingReview) {
      const { data: newMappingReview } = await API.post('/mapping/review', {
        client_id: id,
        file_id,
        version,
        resourcetype,
        namespace,
      });

      if (newMappingReview) {
        this.props.setParentState({ mappingReview: newMappingReview }, () => {
          this.setState({ showRequesterChecklist: true });
        });
      }
    } else {
      this.setState({ showRequesterChecklist: true });
    }
  };

  handleCopyBtnClick = () => {
    const resource = {
      client_name: this.props.client.name,
      client_id: this.props.client.id,
      resourcetype: this.props.state.resourcetype,
      namespace: this.props.state.namespace,
      file: this.props.state.file,
    }
    copyToClipboard(JSON.stringify(resource))
  }

  handleStrucDefClick = async () => {
    logGAEvent('Struture Definition');
    let profile = getImplementationGuide(this.props.state.dataSource);
    let profiles = await getStructureDefProfiles();
    this.setState({
      profiles,
      profile_url: profile ? profile.split('|')[0] : '',
      addMappingModal: true,
    }, () => {});
  }

  render() {
    const filename = `fhir_mapping_${this.props.state.resourcetype}_${this.props.state.namespace}_${
      this.props.state.version
    }_${new Date().toISOString().slice(0, 10)}.csv`;
    const byResourcetypeOptions = this.props.state.selectOptions.resourcetypes;
    const byFileOptions = this.props.state.selectOptions.files;
    const selectedOptions = this.props.state.byResourcetype ? byResourcetypeOptions : byFileOptions;
    const value = this.props.state.byResourcetype
      ? [this.props.state.resourcetype, this.props.state.namespace, this.props.state.file]
      : [this.props.state.file, this.props.state.resourcetype, this.props.state.namespace];

    return (
      <>
        <AddMappingModal
          addMappingModal={this.state.addMappingModal}
          addStructureDefElements={this.addStructureDefElements}
          profileUrl={this.state.profile_url}
          profiles={this.state.profiles}
          structuredefElements={this.props.structuredefElements}
          resourcetype={this.props.state.resourcetype}
          dataSource={this.props.state.dataSource}
          addProfileToMapping={this.addProfileToMapping}
          parentState={this.props.state}
          setParentState={this.setParentState}
        />
        {this.props.state.fileId &&
          this.state.showRequesterChecklist &&
          this.props.state.mappingReview && (
            <ReviewDrawer
              dataSource={this.props.state.dataSource}
              fileId={this.props.state.fileId}
              version={this.props.state.version}
              mappingReviewId={this.props.state.mappingReview.id}
              toggleModal={showModal => this.setState({ showRequesterChecklist: showModal })}
              mappingReview={this.props.state.mappingReview}
              getMappingReview={this.props.getMappingReview}
            />
          )}

        {this.state.showDiffViewer && (
            <DiffModal
              parentState={this.props.state}
              toggleModal={showModal => this.setState({ showDiffViewer: showModal })}
            />
          )}

        <Modal
          title="Create New Mapping"
          visible={this.state.showModal}
          width={1000}
          onCancel={this.closeModalNew}
          destroyOnClose
          footer={null}
        >
          <DataSourceCreate
            addDataSource={this.addDataSource}
            client={this.props.client}
            closeModal={this.closeModalNew}
          />
        </Modal>
        <Row justify="start">
          <Col>
            <Switch
                  checkedChildren="By File"
                  data-cy="mapping-selector-switch-btn"
                  onChange={() =>
                    this.props.setParentState({ byResourcetype: !this.props.state.byResourcetype })
                  }
                  unCheckedChildren="By ResourceType"
                  style={{ marginBottom: 5 }}
                />
          </Col>
        </Row>
        <Row gutter={[8, 16]} align="middle">
          {!this.props.hasNoMappings && (
            <>
              <Col span={12}>

                <Cascader
                  options={selectedOptions}
                  style={{ width: '90%' }}
                  disabled={this.props.state.loading}
                  value={value}
                  data-cy="mapping-selector-btn"
                  allowClear={false}
                  dropdownRender={this.dropdownRender}
                  onChange={this.props.updateSelectedMapping}
                  expandTrigger="hover"
                  placeholder={
                    this.props.state.byResourcetype ? 'Select Resourcetype' : 'Select File'
                  }
                />
                <Button size="large" type="text" style={{ verticalAlign:'baseline', height: '100%'}} onClick={() => this.handleCopyBtnClick()}>
                <CopyOutlined size="large" height="50px" />
              </Button>
              </Col>
              <Col>
                <Hotkey triggerUndo={this.props.undo} triggerRedo={this.props.redo}>
                  {' '}
                </Hotkey>
              </Col>
              <Col flex={1} />

              <>
                <Col>
                  <Modal
                    title="Enter New Version"
                    visible={this.props.showSaveVersionModal}
                    onOk={this.props.handleSaveVersion}
                    confirmLoading={this.props.saveVersionLoading}
                    onCancel={() => this.props.setParentState({ showSaveVersionModal: false })}
                  >
                    <Input
                      value={this.props.state.newVersion}
                      onChange={e => this.props.setParentState({ newVersion: e.target.value })}
                      data-cy="input-new-version"
                    />
                  </Modal>
                </Col>
                <Col>
                  {
                    process.env.REACT_APP_FEATURE_MAP_STAGING &&
                    this.props.state.mappingReview &&
                    this.props.state.mappingReview.status === 'complete' &&
                    this.props.state.version !== 'latest' &&
                    <Button
                      style={{ marginRight: 10 }}
                      onClick={() => this.showMergeVersionConfirm()}
                      data-cy="merge-version-to-latest-btn"
                    >
                      Merge to Latest
                    </Button>
                  }
                  {
                    (typeof this.props.state.mappingReview !== 'undefined' && this.props.state.version !== 'latest') &&
                    <Button
                      style={{ marginRight: 10 }}
                      onClick={this.handleReviewBtnClick}
                      data-cy="toggle-mapping-review-btn"
                    >
                      {this.props.state.mappingReview ? 'Edit Review' : 'Request Review'}
                    </Button>
                  }

                  <Button
                    style={{ marginRight: 10 }}
                    onClick={() => { this.setState({ showDiffViewer: true }); logGAEvent('Compare Versions'); }}
                    data-cy="toggle-diff-viewer-btn"
                  >
                    Compare Versions
                  </Button>

                  <Button
                    style={{ marginRight: 10 }}
                    onClick={this.handleStrucDefClick}
                    data-cy="add-mapping-btn"
                  >
                    StructureDefinition
                  </Button>

                  <Dropdown.Button
                    placement="bottomRight"
                    overlayStyle={{
                      border: '1px solid #d9d9d9',
                      borderRadius: 2,
                    }}
                    icon={<DownOutlined />}
                    onClick={() =>
                      this.handleAdd({
                        business_term: null,
                        driving: null,
                        editable: true,
                        must_support: null,
                        notes: null,
                        id: uuidv4(),
                        number: null,
                        slice: null,
                        type: null,
                        file: this.props.state.file ? this.props.state.file : '',
                        path: `0_${Math.floor(Math.random() * (999 - 100 + 1) + 100)}_PATH`,
                        value: `0_${Math.floor(Math.random() * (999 - 100 + 1) + 100)}_VALUE`,
                        client_id: this.props.state.client.id,
                        version: this.props.state.version,
                        resourcetype: this.props.state.resourcetype,
                        namespace: this.props.state.namespace,
                      })
                    }
                    data-cy="insert-row-btn"
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="2"
                          onClick={() => this.insertMultipleRows(EXTENSION_CODEABLE_CONCEPT)}
                          data-cy="insert-codeable-concept-extension-btn"
                          icon={<FileAddOutlined />}
                          type="primary"
                        >
                          Add Extension (CodeableConcept)
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    Insert Row
                  </Dropdown.Button>
                </Col>
                <Col>
                  <Dropdown.Button
                    placement="bottomLeft"
                    data-cy="mapping-extended-menu-btn"
                    overlayStyle={{
                      border: '1px solid #d9d9d9',
                      borderRadius: 2,
                    }}
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="1"
                          data-cy="create-new-mapping-btn"
                          icon={<PlusOutlined />}
                          type="primary"
                          onClick={() => this.setState({ showModal: true })}
                        >
                          Create New Mapping
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          key="2"
                          onClick={() => this.props.setParentState({ showSaveVersionModal: true })}
                          icon={<SaveOutlined />}
                          type="primary"
                        >
                          Save Version
                        </Menu.Item>
                        <Menu.Item
                          key="9"
                          data-cy="save-as-template-btn"
                          onClick={() => this.showSaveTemplateConfirm()}
                          icon={<SaveOutlined />}
                          type="primary"
                        >
                          Save As Template
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item key="4" icon={<UndoOutlined />} onClick={this.props.undo}>
                          Undo
                          <span
                            style={{
                              float: 'right',
                              color: 'lightgray',
                            }}
                          >
                            Cmd + Z
                          </span>
                        </Menu.Item>
                        <Menu.Item key="5" icon={<RedoOutlined />} onClick={this.props.redo}>
                          Redo
                          <span
                            style={{
                              float: 'right',
                              color: 'lightgray',
                            }}
                          >
                            Cmd + Shift + Z
                          </span>
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          key="3"
                          onClick={() => this.clearMappingCache()}
                          data-cy="insert-codeable-concept-extension-btn"
                          icon={<ClearOutlined />}
                          type="primary"
                        >
                          Clear Mapping Cache (JsonToFHIR)
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          key="6"
                          icon={<DownloadOutlined />}
                          onClick={this.handleDownloadCurrentMappings}
                        >
                          <CsvDownload
                            filename={filename.toLowerCase()}
                            style={{
                              border: 'none',
                              background: 'none',
                              fontSize: '14px',
                              texAlign: 'left',
                              padding: 0,
                            }}
                            data={this.props.state.dataSource}
                          >
                            Export Current Mappings to CSV
                          </CsvDownload>
                        </Menu.Item>
                        <Menu.Item
                          key="7"
                          icon={<ExportOutlined />}
                          onClick={() =>
                            exportAllMappings(this.props.client, this.props.state.version)
                          }
                        >
                          Export All Mappings to CSV
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          key="8"
                          data-cy="duplicate-mapping-btn"
                          icon={<CopyOutlined />}
                          onClick={this.showDuplicateConfirm}
                        >
                          Duplicate Mappings
                        </Menu.Item>
                        <Menu.Item
                          key="9"
                          data-cy="delete-mapping-bulk-btn"
                          icon={<DeleteOutlined />}
                          onClick={this.showDeleteConfirm}
                        >
                          Delete Mappings (Bulk)
                        </Menu.Item>
                      </Menu>
                    }
                  />
                </Col>
              </>
            </>
          )}

          {this.props.hasNoMappings && (
            <Button
              data-cy="no-mappings-create-new-mapping-btn"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => this.setState({ showModal: true })}
            >
              Create New Mapping
            </Button>
          )}
        </Row>
      </>
    );
  }
}

export default withRouter(TableHeader);
